// ManageAnnouncementsNew.tsx - Functional Component
import PaginationControls from "components/PaginationControls";
import { RouteProps } from "containers/App";
import Constants from "helpers/constants";
import { Announcement as AnnouncementProps } from "interfaces/announcement";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { connect, ConnectedProps, useSelector } from "react-redux";
import { State } from "state";
import { createAnnouncement, deleteAnnouncement } from "state/announcement";
import { fetchMyAnnouncements } from "state/announcements";
import { trackPageView, PAGEVIEW_EVENTS } from "state/userEvents";
import Outline from "../assets/img/-outline.svg";
import eye from "../assets/img/eye.svg";
import FilePreviewModal from "components/FilePreviewModal";
import BackLink from "components/BackLink";
import ProjectFilter, { FilterOptions } from "containers/ProjectFilter";
// ==================== Announcement List Components ====================

interface AnnouncementItemProps {
  announcement: AnnouncementProps;
}

const AnnouncementItem: React.FC<AnnouncementItemProps> = ({ announcement }) => {
  const [previewModal, setPreviewModal] = useState<{
    isOpen: boolean;
    url: string;
    name: string;
  }>({
    isOpen: false,
    url: "",
    name: "",
  });

  const parsedBody = (() => {
    try {
      return typeof announcement.body === "string" && announcement.body.startsWith('"')
        ? JSON.parse(announcement.body)
        : announcement.body;
    } catch {
      return announcement.body;
    }
  })();

  const stripHtml = (html: string) => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const contentPreview = stripHtml(parsedBody).substring(0, 1000);

  const isPreviewable = (url: string): boolean => {
    const extension = url.split(".").pop()?.toLowerCase() || "";
    const previewableTypes = ["jpg", "jpeg", "png", "gif", "webp", "pdf"];
    return previewableTypes.includes(extension);
  };

  const handleAttachmentClick = (url: string, name?: string, index?: number) => {
    const fileName = name || url.split("/").pop() || "File";
    // Build API URL with file index for correct attachment
    const apiUrl = announcement.id && typeof index === 'number'
      ? `${process.env.REACT_APP_API_SERVICE_URL || ''}/api/announcement-file/${announcement.id}/${index}`
      : url;
    if (isPreviewable(url)) {
      // Open preview modal for images and PDFs
      setPreviewModal({
        isOpen: true,
        url: apiUrl,
        name: fileName,
      });
    } else {
      // Direct download for other file types
      handleDownload(apiUrl, fileName);
    }
  };

  const handleDownload = (url: string, fileName: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClosePreview = () => {
    setPreviewModal({
      isOpen: false,
      url: "",
      name: "",
    });
  };

  return (
    <div style={styles.announcementItem}>
      <div style={styles.announcementHeader}>
        <p style={styles.announcementTitle}>
          {announcement.title.slice(0, 1).toLocaleUpperCase() + announcement.title.slice(1)}
        </p>
        <p style={styles.chainName}>
          {announcement.chain.name} - {announcement.project.name}
        </p>

        <p style={styles.dateStyle}>
          {moment(announcement.dateCreated).format("M/DD/YYYY")} -{" "}
          {`By ${announcement.user.firstName} ${announcement.user.lastName}`}
        </p>
      </div>
      <div style={styles.announcementBody}>
        {contentPreview}
        {parsedBody.length > 1000 ? "..." : ""}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 5, justifyContent: "flex-start" }}>
        {announcement.allAttachments?.map((item, index) => {
          const canPreview = isPreviewable(item.name);
          const apiUrl = announcement.id !== undefined ? `${process.env.REACT_APP_API_SERVICE_URL || ''}/api/announcement-file/${announcement.id}/${index}` : (item.name);

          return (
            <div
              style={styles.attachmentButton}
              key={index}
              onClick={() => handleAttachmentClick(apiUrl, item.name, index)}
              title={`${item.name} - ${canPreview ? "Click to preview" : "Click to download"}`}
            >
              {canPreview ? (
                <img
                  src={eye}
                  alt="preview"
                  style={{ width: "100%", height: "100%", cursor: "pointer" }}
                />
              ) : (
                <img
                  src={Outline}
                  alt="download"
                  style={{ width: "100%", height: "100%", cursor: "pointer" }}
                />
              )}
            </div>
          );
        })}
      </div>

      <FilePreviewModal
        isOpen={previewModal.isOpen}
        fileUrl={previewModal.url}
        fileName={previewModal.name}
        onClose={handleClosePreview}
      />
    </div>
  );
};

interface AnnouncementListProps {
  data: AnnouncementProps[];
}

const AnnouncementList: React.FC<AnnouncementListProps> = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div style={styles.emptyState}>
        <p style={{ color: "#999", fontSize: 14 }}>No announcements yet</p>
      </div>
    );
  }

  return (
    <div style={styles.listContainer}>
      {data.map((announcement, index) => (
        <div>
          <AnnouncementItem key={announcement.id} announcement={announcement} />
          <div style={styles.dashedBorder}></div>
        </div>
      ))}
    </div>
  );
};

// ==================== Main Component ====================

type ManageAnnouncementsProps = PropsFromRedux & RouteProps;

const MyAnnouncements: React.FC<ManageAnnouncementsProps> = (props) => {
  const { user, announcements = [], isMobile } = props;

  const { location, history } = props;
  const queryParams = new URLSearchParams(location.search);
  const projectIdFromUrl = queryParams.get("projectId");
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(
    projectIdFromUrl ? Number(projectIdFromUrl) : null
  );

  const authUser = useSelector((state: State) => state.auth_user.content);
  const userRole = authUser?.role;

  const [currentPage, setCurrentPage] = useState(1);
  const [resultsPerPage, setResultsPerPage] = useState(5);

  const filteredAnnouncements = useMemo(() => {
  if (!selectedProjectId) return [];
  return announcements.filter(
    (a) => a.project?.id === selectedProjectId
  );
}, [announcements, selectedProjectId]);

  const paginatedAnnouncements = useMemo(() => {
    const startIndex = (currentPage - 1) * resultsPerPage;
    const endIndex = startIndex + resultsPerPage;
    return filteredAnnouncements.slice(startIndex, endIndex);
  }, [filteredAnnouncements, currentPage, resultsPerPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleResultsPerPageChange = (perPage: number) => {
    setResultsPerPage(perPage);
    setCurrentPage(1);
  };

  useEffect(() => {
    props.fetchMyAnnouncements().then(() => {
      props.trackPageView({
        event_type: PAGEVIEW_EVENTS.ANNOUNCEMENTS_PAGEVIEW,
        user_id: user?.id,
        store_id: null,
        project_id: null,
        timestamp: Date.now(),
      });
    });
  }, []);

  return (
    <div style={styles.pageWrapper(isMobile)}>

      <div style={styles.contentContainer}>
        <div style={styles.listArea}>
          <h1 style={styles.title}>Project Announcements</h1>

          <div style={{ padding: "0 15px 15px 15px", maxWidth: 350 }}>
            <ProjectFilter
              name="filter-project"
              value={selectedProjectId ?? -1}
              isSearchable
              minHeight={44}
              noShadow
              onChange={({ id }: FilterOptions) => {
                if (id === -1) {
                  setSelectedProjectId(null);
                  setCurrentPage(1);
                  return;
                }
                setSelectedProjectId(id);
                setCurrentPage(1);

                if (id) {
                  props.history.replace(`?projectId=${id}`);
                } else {
                  props.history.replace("");
                }
              }}
            />
          </div>

          {!selectedProjectId ? (
            <div style={{ padding: 20, color: "#999", fontSize: 14, minHeight: "50vh", textAlign: "center" }}>
              Please select a project to view announcements
            </div>
          ) : (
          <>
          <AnnouncementList data={paginatedAnnouncements} />

          <PaginationControls
            totalItems={filteredAnnouncements.length}
            currentPage={currentPage}
            resultsPerPage={resultsPerPage}
            onPageChange={handlePageChange}
            onResultsPerPageChange={handleResultsPerPageChange}
          />
          </>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== Redux Connection ====================

const connector = connect(
  (state: State) => ({
    user: state.auth_user.content,
    announcements: state.announcements.content,
    hasErrors: state.surveys.hasError,
    errors: state.surveys.errors,
    isMobile: state.breakpoint.content === Constants.BREAKPOINT_MOBILE,
    currentUserId: state.auth_user.content?.id,
    isAdmin: state.auth_user.content?.role === Constants.AB_SYSTEM_ADMIN,
  }),
  {
    fetchMyAnnouncements,
    createAnnouncement,
    deleteAnnouncement,
    trackPageView,
  }
);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(MyAnnouncements);

// ==================== Styles ====================

const styles = {
  pageWrapper: (isMobile: boolean): React.CSSProperties => ({
    width: "100%",
    height: "100%",
    minHeight: "100vh",
    // backgroundColor: "#f5f5f5",
    // padding: 20,
    boxSizing: "border-box",
    margin: isMobile ? 0 : "0 auto",
    // marginBottom: "30px",
    // paddingBottom: "30px",
    // borderRadius: "16px",
  }),
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 20,
    width: "100%",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    border: "1px solid #eee",
    borderRadius: "16px",
    backgroundColor: "#fff",
    marginBottom: "30px",
  } as React.CSSProperties,
  // formArea: {
  //   flex: 2,
  // } as React.CSSProperties,
  listArea: {
    flex: 1,
    height: "100%",
    overflowY: "auto",
    // backgroundColor: "green",
  } as React.CSSProperties,
  listContainer: {
    padding: 0,
    // height: "100%",
    paddingBottom: "100px",
  } as React.CSSProperties,
  title: {
    fontWeight: 500,
    fontSize: "20px",
    lineHeight: "130%",
    padding: 15,
  } as React.CSSProperties,
  listTitle: {
    fontSize: 20,
    fontWeight: 600,
    color: "#333",
    marginBottom: 20,
    marginTop: 0,
  } as React.CSSProperties,
  announcementItem: {
    // backgroundColor: "#ffc5c50",
    padding: 15,
    // borderRadius: 16,
    marginBottom: 15,
    // boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    // border: "1px solid #eee",
  } as React.CSSProperties,
  dashedBorder: {
    // height: "10px",
    // width: "100%",

    margin: "0 100px",
    borderBottom: "1px dashed #929497",
    borderWidth: "1.5px",
  } as React.CSSProperties,
  announcementHeader: {
    color: "#666",
    fontSize: 12,
    // marginBottom: 8,
  } as React.CSSProperties,
  announcementTitle: {
    fontSize: 14,
    fontWeight: 400,
    // marginBottom: 10,
    color: "#333",
    padding: 0,
    margin: 0,
  } as React.CSSProperties,
  chainName: {
    fontWeight: 500,
    // fontStyle: Medium,
    fontSize: 16,
    color: "#000",
    padding: 0,
    margin: 0,
    lineHeight: 1.4,
  } as React.CSSProperties,
  dateStyle: {
    fontWeight: 400,
    fontSize: "12px",
    color: "#929497",
    padding: 0,
    margin: "5px 0 5px 0",
  } as React.CSSProperties,
  announcementBody: {
    fontSize: 12,
    lineHeight: 1.6,
    color: "#000",
    // marginBottom: 10,
    fontWeight: 400,
  } as React.CSSProperties,

  emptyState: {
    backgroundColor: "#fff",
    padding: 40,
    borderRadius: 16,
    textAlign: "center",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    border: "1px solid #eee",
  } as React.CSSProperties,

  attachmentButton: {
    height: "20px",
    width: "20px",
    cursor: "pointer",
    transition: "transform 0.2s",
  } as React.CSSProperties,

  attachmentButtonHover: {
    transform: "scale(1.1)",
  } as React.CSSProperties,
};
